// Document Status Configuration - UNIFIED APPROACH
// Manages document statuses (Draft, Review, Published, Deprecated, etc.)
// Based on Prisma DocumentStatus model schema

import { createTrackingConfig } from '../../../components/generic/labels';

export const documentStatusConfig = {
  entityName: 'DocumentStatus',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/document-status',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // Document Status specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // Document statuses typically don't have lookup dependencies
      // but you could add workflow types, categories, etc.
    }
  },
  
  // UI Configuration
  ui: {
    drawerWidth: '600px',
    defaultPageSize: 15,
    pageSizeOptions: [15, 25, 50, 100],
    defaultTableDensity: 'normal',
    requiredFieldStyle: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'documentStatusService',
    methods: {
      getAll: 'getAllDocumentStatuses',
      getById: 'getDocumentStatusById', 
      create: 'createDocumentStatus',
      update: 'updateDocumentStatus',
      delete: 'deleteDocumentStatus',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'Status Name', 
      type: 'text', 
      required: true,
      table: { header: 'Status Name', width: '200px', sortable: true },
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 50,
        pattern: /^[A-Za-z\s]+$/,
        message: 'Status name must contain only letters and spaces'
      }
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      required: false,
      table: { header: 'Description', width: '300px', sortable: false },
      validation: { 
        required: false, 
        maxLength: 255 
      }
    },
    { 
      name: 'color', 
      label: 'Display Color', 
      type: 'color', 
      required: false,
      placeholder: 'Choose a color or enter hex/name',
      table: { 
        header: 'Color', 
        width: '100px', 
        sortable: false,
        cellRenderer: 'color'
      },
      validation: { 
        required: false,
        pattern: /^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})|[a-z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      }
    },
    { 
      name: 'record_order', 
      label: 'Sort Order', 
      type: 'number', 
      required: false,
      table: { header: 'Order', width: '80px', sortable: true },
      validation: { 
        required: false, 
        min: 0, 
        max: 1000 
      }
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Active', 
        width: '80px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      validation: { required: false }
    },
    // User Relations (hidden, used for tracking)

    { 
      name: 'createdBy', 
      label: 'Created By', 
      type: 'text', 
      readOnly: true,
      table: false, // Don't show in main table
      validation: { required: false }
    },

    { 
      name: 'updatedBy', 
      label: 'Updated By', 
      type: 'text', 
      readOnly: true,
      table: false, // Don't show in main table
      validation: { required: false }
    },

    // System Fields (read-only)
    { 
      name: 'createdAt', 
      label: 'Created Date', 
      type: 'date', 
      readOnly: true,
      table: false, //{ header: 'Created', width: '120px', sortable: true },
      validation: { required: false }
    },
    { 
      name: 'updatedAt', 
      label: 'Updated Date', 
      type: 'date', 
      readOnly: true,
      table: false, //{ header: 'Updated', width: '120px', sortable: true },
      validation: { required: false }
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'isActive'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'isActive']
    },
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false] // Active first
  },

  // Messages Configuration
  messages: {
    loading: 'Loading document statuses...',
    noData: 'No document statuses found. Click "Add Status" to create your first status.',
    loadError: 'Failed to load document statuses',
    saveSuccess: 'Document status saved successfully!',
    saveError: 'Failed to save document status',
    deleteSuccess: 'Document status deleted successfully!',
    deleteError: 'Failed to delete document status',
    deleteConfirm: 'Are you sure you want to delete this document status?'
  },

  // Form Layout Configuration
  layout: [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: ['name', 'description']
    },
    {
      title: 'Display & Settings',
      icon: '⚙️',
      fields: ['color', 'record_order', 'isActive']
    }
  ],

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('DocumentStatus'),

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'Document Status',
    plural: 'Document Statuses',
    addButton: 'Add Status',
    editButton: 'Edit Status',
    deleteButton: 'Delete Status'
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    'createdAt', 'updatedAt', 'createdBy', 'updatedBy'
  ],

  // Export filename for data exports
  exportFileName: 'document_statuses',

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
